import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';

interface BoolFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

const BOOL_FILTER_OPERATORS: BoolFilterOperatorOption[] = [
  { value: 'eq', label: 'Equal to' },
  { value: 'ne', label: 'Not equal to' },
];

@Component({
  selector: 'app-bool-filter',
  templateUrl: './bool-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppBoolFilterComponent), multi: true },
  ],
})
export class AppBoolFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = BOOL_FILTER_OPERATORS;

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;
  value: boolean | null = null;

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(400)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    this.operator = op;
    this.value = null;
    this.emit({ operator: op, value: undefined });
  }

  onValueChange(value: boolean | null): void {
    this.value = value;
    this.emit({ value: value == null ? undefined : `${value}` });
  }

  clear(): void {
    this.value = null;
    this.emit({ value: undefined });
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;

    const raw = this.field?.value;
    this.value = raw === 'true' ? true : raw === 'false' ? false : null;
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
